import { create } from 'zustand';
import { apiService } from '../services/api';

// Main store for VPL data
const useStore = create((set, get) => ({
  // Loading states
  loading: {
    players: false,
    seasons: false,
    teams: false,
    seasonTeams: false,
    pointsTable: false,
    playerStats: false,
    fixtures: false,
    groupStages: false,
  },
  // Data states
  players: [],
  seasons: [],
  teams: [],
  seasonTeams: {},
  pointsTables: {},
  playerStats: {},
  fixtures: {}, // Store fixtures by season ID
  groupStages: {}, // Store group stages by season ID
  allMatches: {}, // Store all matches with player stats by season ID

  // Current selections
  currentSeason: null,

  // Actions
  setLoading: (key, value) =>
    set((state) => ({
      loading: { ...state.loading, [key]: value },
    })),

  setError: (key, error) =>
    set((state) => ({
      errors: { ...state.errors, [key]: error },
    })),

  setCurrentSeason: (season) => set({ currentSeason: season }),

  // Fetch all initial data
  // OPTIMIZED: only fetch seasons + teams on startup (fast, <10kb each).
  // players (176kb) and seasonTeams are fetched lazily by the pages that need them.
  fetchInitialData: async () => {
    const { fetchSeasons, fetchTeams } = get();
    try {
      await Promise.all([fetchSeasons(), fetchTeams()]);
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
    }
  },

  // Players actions
  fetchPlayers: async () => {
    // Cache check — 176kb payload, skip if already loaded
    if (get().players.length > 0) return get().players;

    set((state) => ({ loading: { ...state.loading, players: true } }));
    try {
      const response = await apiService.getSeasonPlayers();
      set({
        players: response.data || [],
        errors: { ...get().errors, players: null },
      });
    } catch (error) {
      console.error('❌ Error fetching players:', error);
      set((state) => ({
        errors: { ...state.errors, players: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, players: false } }));
    }
  },

  createPlayer: async (playerData) => {
    try {
      const response = await apiService.createPlayer(playerData);
      set((state) => ({
        players: [...state.players, response.data],
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error creating player:', error);
      throw error;
    }
  },

  // Seasons actions
  fetchSeasons: async () => {
    set((state) => ({ loading: { ...state.loading, seasons: true } }));
    try {
      const response = await apiService.getSeasons();
      const seasons = response.data || [];
      set({
        seasons,
        errors: { ...get().errors, seasons: null },
      });

      // Always update currentSeason to the latest season so new Supabase
      // columns (show_prize_pool, prize_pool_display, etc.) are reflected
      // immediately after a re-fetch without needing a hard refresh.
      // Priority: registration_start_date → start_date → end_date
      if (seasons.length > 0) {
        const getPriority = (s) =>
          new Date(s.registration_start_date || s.start_date || s.end_date || 0).getTime();
        const sortedSeasons = [...seasons].sort((a, b) => getPriority(b) - getPriority(a));
        set({ currentSeason: sortedSeasons[0] });
      }
    } catch (error) {
      console.error('❌ Error fetching seasons:', error);
      set((state) => ({
        errors: { ...state.errors, seasons: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, seasons: false } }));
    }
  },

  createSeason: async (seasonData) => {
    try {
      const response = await apiService.createSeason(seasonData);
      set((state) => ({
        seasons: [...state.seasons, response.data],
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error creating season:', error);
      throw error;
    }
  },

  // Teams actions
  fetchTeams: async () => {
    set((state) => ({ loading: { ...state.loading, teams: true } }));
    try {
      const response = await apiService.getTeams();
      set({
        teams: response.data || [],
        errors: { ...get().errors, teams: null },
      });
    } catch (error) {
      console.error('❌ Error fetching teams:', error);
      set((state) => ({
        errors: { ...state.errors, teams: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, teams: false } }));
    }
  },

  createTeam: async (teamData) => {
    try {
      const response = await apiService.createTeam(teamData);
      set((state) => ({
        teams: [...state.teams, response.data],
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error creating team:', error);
      throw error;
    }
  },

  // Season teams actions
  fetchSeasonTeams: async () => {
    // Cache check — seasonTeams is a dict keyed by season id; skip if already populated
    const existing = get().seasonTeams;
    if (existing && Object.keys(existing).length > 0) return existing;

    set((state) => ({ loading: { ...state.loading, seasonTeams: true } }));
    try {
      const response = await apiService.getSeasonAllTeams();
      set((state) => ({
        seasonTeams: { ...response.data },
        errors: { ...state.errors, seasonTeams: null },
      }));
    } catch (error) {
      set((state) => ({
        errors: { ...state.errors, seasonTeams: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, seasonTeams: false } }));
    }
  },

  // Points table actions
  fetchPointsTable: async (seasonId) => {
    if (!seasonId) return;

    set((state) => ({ loading: { ...state.loading, pointsTable: true } }));
    try {
      const response = await apiService.getSeasonPointsTable(seasonId);
      set((state) => ({
        pointsTables: { ...state.pointsTables, [seasonId]: response.data },
        errors: { ...state.errors, pointsTable: null },
      }));
    } catch (error) {
      console.error('❌ Error fetching points table:', error);
      set((state) => ({
        errors: { ...state.errors, pointsTable: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, pointsTable: false } }));
    }
  },

  // Player stats actions
  fetchPlayerStats: async (seasonId) => {
    if (!seasonId) return;

    set((state) => ({ loading: { ...state.loading, playerStats: true } }));
    try {
      const response = await apiService.getSeasonPlayerData(seasonId);
      set((state) => ({
        playerStats: { ...state.playerStats, [seasonId]: response.data || [] },
        errors: { ...state.errors, playerStats: null },
      }));
    } catch (error) {
      console.error('❌ Error fetching player stats:', error);
      set((state) => ({
        errors: { ...state.errors, playerStats: error.message },
      }));
    } finally {
      set((state) => ({ loading: { ...state.loading, playerStats: false } }));
    }
  },

  // Fixtures actions
  fetchFixtures: async (seasonId) => {
    if (!seasonId) return;

    // Check if already loaded
    const existingData = get().fixtures[seasonId];
    if (existingData) {
      return existingData;
    }

    set((state) => ({ loading: { ...state.loading, fixtures: true } }));
    try {
      const response = await apiService.getFixtures(seasonId);
      set((state) => ({
        fixtures: { ...state.fixtures, [seasonId]: response.data },
        errors: { ...state.errors, fixtures: null },
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching fixtures:', error);
      set((state) => ({
        errors: { ...state.errors, fixtures: error.message },
      }));
      throw error;
    } finally {
      set((state) => ({ loading: { ...state.loading, fixtures: false } }));
    }
  },

  // Group stages actions
  fetchGroupStages: async (seasonId) => {
    if (!seasonId) return;

    // Check if already loaded
    const existingData = get().groupStages[seasonId];
    if (existingData) {
      return existingData;
    }

    set((state) => ({ loading: { ...state.loading, groupStages: true } }));
    try {
      const response = await apiService.getGroupStages(seasonId);
      set((state) => ({
        groupStages: { ...state.groupStages, [seasonId]: response.data },
        errors: { ...state.errors, groupStages: null },
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching group stages:', error);
      set((state) => ({
        errors: { ...state.errors, groupStages: error.message },
      }));
      throw error;
    } finally {
      set((state) => ({ loading: { ...state.loading, groupStages: false } }));
    }
  },

  // All matches with player stats actions
  fetchAllMatches: async (seasonId) => {
    if (!seasonId) return;

    // Check if already loaded
    const existingData = get().allMatches[seasonId];
    if (existingData) {
      return existingData;
    }

    set((state) => ({ loading: { ...state.loading, fixtures: true } }));
    try {
      const response = await apiService.getAllMatches(seasonId);
      set((state) => ({
        allMatches: { ...state.allMatches, [seasonId]: response.data },
        errors: { ...state.errors, allMatches: null },
      }));
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching all matches:', error);
      set((state) => ({
        errors: { ...state.errors, allMatches: error.message },
      }));
      throw error;
    } finally {
      set((state) => ({ loading: { ...state.loading, fixtures: false } }));
    }
  },

  // Season-specific data loader
  // OPTIMIZED: fetchPlayerStats and fetchAllMatches are excluded — heavy calls
  // loaded only by the pages that need them (Fixtures.jsx, Players.jsx).
  loadSeasonData: async (seasonId) => {
    if (!seasonId) return;

    const { fetchPointsTable, fetchGroupStages } = get();
    try {
      await Promise.all([
        fetchPointsTable(seasonId),
        fetchGroupStages(seasonId),
      ]);
    } catch (error) {
      console.error('❌ Error loading season data:', error);
    }
  },

  // Getters
  getSeasonTeams: (seasonId) => get().seasonTeams[seasonId] || [],
  getPointsTable: (seasonId) => get().pointsTables[seasonId] || null,
  getPlayerStats: (seasonId) => get().playerStats[seasonId] || [],
  getFixtures: (seasonId) => get().fixtures[seasonId] || null,
  getGroupStages: (seasonId) => get().groupStages[seasonId] || null,
  getAllMatches: (seasonId) => get().allMatches[seasonId] || null,

  // Helper to get current season data
  getCurrentSeasonTeams: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getSeasonTeams(currentSeason.id) : [];
  },

  getCurrentPointsTable: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getPointsTable(currentSeason.id) : null;
  },

  getCurrentPlayerStats: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getPlayerStats(currentSeason.id) : [];
  },

  getCurrentFixtures: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getFixtures(currentSeason.id) : null;
  },

  getCurrentGroupStages: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getGroupStages(currentSeason.id) : null;
  },

  getCurrentAllMatches: () => {
    const currentSeason = get().currentSeason;
    return currentSeason ? get().getAllMatches(currentSeason.id) : null;
  },
}));

export default useStore;